<template>
  <AdminLayout>
    <template #content>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h1 class="h4 mb-1">
            Catégorie
            <span v-if="category" class="text-muted">#{{ category.name }}</span>
          </h1>
          <div v-if="category" class="small text-muted">
            Créée le {{ $format.datetime(category.created_at) }}
          </div>
        </div>

        <div class="d-flex gap-2">
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm btn-icon-label"
            title="Retour à la liste"
            @click="$router.push({ name: 'Categories' })"
          >
            <i class="fa fa-arrow-left"></i>
            <span class="btn-label ms-1">Retour à la liste</span>
          </button>
          <button
            type="button"
            class="btn btn-outline-primary btn-sm btn-icon-label"
            :disabled="!category"
            title="Associer des produits"
            @click="$router.push({ name: 'CategoryProductAssignment', params: { id: category.id } })"
          >
            <i class="fa fa-link"></i>
            <span class="btn-label ms-1">Associer des produits</span>
          </button>
          <button
            type="button"
            class="btn btn-outline-primary btn-sm btn-icon-label"
            :disabled="!category"
            title="Modifier"
            @click="openEditDialog"
          >
            <i class="fa fa-pen"></i>
            <span class="btn-label ms-1">Modifier</span>
          </button>
        </div>
      </div>

      <div v-if="errorMessage" class="alert alert-danger py-2">
        {{ errorMessage }}
      </div>

      <div v-if="isLoading" class="text-center my-5">
        <i class="fa fa-spinner fa-spin fa-2x mb-2"></i>
        <div class="small text-muted">Chargement de la catégorie...</div>
      </div>

      <div v-else-if="!category && !errorMessage" class="alert alert-warning">
        Catégorie introuvable.
      </div>

      <div v-else-if="category" class="row g-3 align-items-start">
        <!-- Infos catégorie -->
        <div class="col-lg-4">
          <div class="card border-0 shadow-sm">
            <div class="card-header bg-white border-0 pb-1 d-flex justify-content-between">
              <h6 class="mb-0">Informations catégorie</h6>
              <span
                class="badge"
                :class="category.is_active ? 'bg-success' : 'bg-secondary'"
              >
                {{ category.is_active ? 'Actif' : 'Inactif' }}
              </span>
            </div>
            <div class="card-body pt-2 pb-3">
              <div class="mb-3">
                <div class="small text-muted mb-1">Image</div>
                <div v-if="category.image_url" class="category-image-preview">
                  <img :src="category.image_url" :alt="`Image ${category.name}`" />
                </div>
                <div v-else class="text-muted small">Aucune image renseignée.</div>
              </div>

              <div class="mb-2">
                <div class="small text-muted">Nom</div>
                <div class="fw-semibold">{{ category.name }}</div>
              </div>

              <div v-if="category.code" class="mb-2">
                <div class="small text-muted">Code</div>
                <div>{{ category.code }}</div>
              </div>

              <div class="mb-2">
                <div class="small text-muted">Position</div>
                <div>{{ category.position ?? 0 }}</div>
              </div>

              <div v-if="category.description" class="mt-2">
                <div class="small text-muted">Description</div>
                <div>{{ category.description }}</div>
              </div>

              <hr />

              <div class="mb-1 d-flex justify-content-between">
                <span class="small text-muted">Nombre de produits</span>
                <span class="fw-semibold">{{ products.length }}</span>
              </div>
              <div class="mb-1 d-flex justify-content-between">
                <span class="small text-muted">Valeur de stock (vente)</span>
                <span class="fw-bold">
                  {{ $format.currency(totalSaleValue) }}
                </span>
              </div>
              <div class="mb-1 d-flex justify-content-between">
                <span class="small text-muted">Valeur de stock (achat)</span>
                <span class="fw-bold">
                  {{ $format.currency(totalPurchaseValue) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Produits de la catégorie -->
        <div class="col-lg-8">

            <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
              <h6 class="mb-0">Produits de cette catégorie</h6>
              <small class="text-muted" v-if="products.length">
                {{ products.length }} produit(s)
              </small>
            </div>

              <BootstrapDataTable
                :rows="products"
                :columns="productTableColumns"
                :sort-by="productTable.sortBy"
                :sort-dir="productTable.sortDir"
                detail-route-name="ProductShow"
                empty-text="Aucun produit associé à cette catégorie."
                @sort-change="handleProductSortChange"
                @refresh="fetchCategory"
              >
                <template #cell-name="{ row }">
                  <div class="fw-semibold">{{ row.name }}</div>
                  <small class="text-muted">{{ row.sku || '-' }}</small>
                </template>

                <template #cell-stock_quantity="{ row }">
                  <span
                    class="badge"
                    :class="row.stock_quantity <= 0 ? 'bg-danger' : 'bg-success'"
                  >
                    {{ row.stock_quantity ?? 0 }}
                  </span>
                </template>

                <template #cell-sale_price="{ row }">
                  {{ $format.currency(row.sale_price || 0) }}
                </template>

                <template #cell-purchase_price="{ row }">
                  {{ $format.currency(row.purchase_price || 0) }}
                </template>

                <template #cell-is_active="{ row }">
                  <span class="badge" :class="row.is_active ? 'bg-success' : 'bg-secondary'">
                    {{ row.is_active ? 'Actif' : 'Inactif' }}
                  </span>
                </template>
              </BootstrapDataTable>

              <div v-if="products.length === 0" class="text-center small text-muted mt-2">
                Aucun produit associé à cette catégorie.
              </div>


        </div>
      </div>

      <!-- Dialog édition -->
      <PrimevueDialog
        v-model:visible="formDialogVisible"
        :modal="true"
        :style="{ width: '600px' }"
        :closable="false"
      >
        <template #header>
          <h5 class="mb-0">Modifier la catégorie</h5>
        </template>

        <CategoryForm
          v-if="category"
          :mode="'edit'"
          :category="category"
          :submitting="isSavingForm"
          @save="handleSave"
          @cancel="closeFormDialog"
        />
      </PrimevueDialog>
    </template>
  </AdminLayout>
</template>

<script>
import CategoryForm from './CategoryForm.vue';

export default {
  name: 'CategoryShowView',
  components: {
    CategoryForm,
  },
  data() {
    return {
      category: null,
      products: [],
      isLoading: false,
      errorMessage: '',
      successMessage: '',
      formDialogVisible: false,
      isSavingForm: false,
      productTable: {
        sortBy: 'name',
        sortDir: 'asc',
      },
      productTableColumns: [
        { key: 'name', label: 'Produit', sortable: true },
        { key: 'stock_quantity', label: 'Stock', sortable: true, class: 'text-center' },
        { key: 'sale_price', label: 'P. vente', sortable: true, class: 'text-end' },
        { key: 'purchase_price', label: 'P. achat', sortable: true, class: 'text-end' },
        { key: 'is_active', label: 'Statut', sortable: true, class: 'text-center' },
      ],
    };
  },
  computed: {
    totalSaleValue() {
      return this.products.reduce((sum, p) => {
        const qte = p.stock_quantity ?? 0;
        const price = p.sale_price ?? 0;
        return sum + qte * price;
      }, 0);
    },
    totalPurchaseValue() {
      return this.products.reduce((sum, p) => {
        const qte = p.stock_quantity ?? 0;
        const price = p.purchase_price ?? 0;
        return sum + qte * price;
      }, 0);
    },
  },
  mounted() {
    this.fetchCategory();
  },
  methods: {
    async fetchCategory() {
      const id = this.$route.params.id;

      this.isLoading = true;
      this.errorMessage = '';
      this.successMessage = '';
      this.category = null;
      this.products = [];

      try {
        const res = await this.$api.get(`/categories/${id}`);
        const data = res.data.data;
        this.category = {
          id: data.id,
          name: data.name,
          code: data.code,
          image_url: data.image_url,
          position: data.position,
          is_active: data.is_active,
          description: data.description,
          created_at: data.created_at,
        };
        this.products = data.products || [];
      } catch (error) {
        console.error('Erreur chargement catégorie', error);
        this.errorMessage = 'Impossible de charger cette catégorie.';
      } finally {
        this.isLoading = false;
      }
    },

    handleProductSortChange({ sortBy, sortDir }) {
      this.productTable.sortBy = sortBy;
      this.productTable.sortDir = sortDir;
    },

    openEditDialog() {
      if (!this.category) return;
      this.formDialogVisible = true;
    },

    closeFormDialog() {
      this.formDialogVisible = false;
    },

    async handleSave(payload) {
      if (!this.category) return;
      if (this.isSavingForm) return;

      this.errorMessage = '';
      this.successMessage = '';
      this.isSavingForm = true;

      try {
        const isFormData = typeof FormData !== 'undefined' && payload instanceof FormData;
        const config = isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined;

        if (isFormData) {
          await this.$api.post(`/categories/${this.category.id}`, payload, config);
        } else {
          await this.$api.put(`/categories/${this.category.id}`, payload);
        }
        this.successMessage = 'Catégorie mise à jour avec succès.';
        this.formDialogVisible = false;
        await this.fetchCategory();
      } catch (error) {
        console.error('Erreur mise à jour catégorie', error);
        const errors = error.response && error.response.data && error.response.data.errors;
        const msg = errors
          ? Object.values(errors).flat().join(' ')
          : 'Une erreur est survenue lors de la mise à jour de la catégorie.';
        this.errorMessage = msg;
      } finally {
        this.isSavingForm = false;
      }
    },
  },
};
</script>

<style scoped>
.card {
  border-radius: 0.75rem;
}

.category-image-preview {
  align-items: center;
  background: #f8fafc;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 0.75rem;
  display: inline-flex;
  height: 6rem;
  justify-content: center;
  overflow: hidden;
  width: 6rem;
}

.category-image-preview img {
  height: 100%;
  object-fit: cover;
  width: 100%;
}
</style>
